import React, { useMemo } from 'react';
import { Card, Row, Col, List, Tag, Empty, Button, Space, Timeline, Progress, Avatar, Divider } from 'antd';
import { 
  ProjectOutlined, 
  FilePdfOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  RightOutlined,
  PlusOutlined,
  UploadOutlined,
  FolderOpenOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import '../styles/app-redesign.css';

const Dashboard = React.memo(({ projects, documents, onNavigate }) => {
  // Calculate metrics with useMemo to avoid recalculation on every render
  const metrics = useMemo(() => {
    const activeProjects = projects.filter(p => p.is_active);
    const totalProjects = projects.length;
    const totalDocuments = documents.length;
    const processedDocs = documents.filter(d => d.processed).length;
    const pendingDocs = documents.filter(d => !d.processed).length;
    const processingRate = totalDocuments > 0 ? Math.round((processedDocs / totalDocuments) * 100) : 0;
    
    const totalStorage = documents.reduce((sum, doc) => sum + (doc.file_size || 0), 0);
    const totalStorageMB = (totalStorage / 1024 / 1024).toFixed(2);

    return {
      activeProjects,
      totalProjects,
      totalDocuments,
      processedDocs,
      pendingDocs,
      processingRate,
      totalStorageMB
    };
  }, [projects, documents]);

  // Memoize recent projects and documents
  const recentProjects = useMemo(() => 
    projects
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5),
    [projects]
  );

  const recentDocuments = useMemo(() => 
    documents
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5),
    [documents]
  );

  // Memoize activity timeline
  const activityTimeline = useMemo(() => {
    const activities = [
      ...projects.slice(0, 3).map(p => ({
        type: 'project',
        title: `Project created: ${p.name}`,
        time: new Date(p.created_at),
        icon: <ProjectOutlined style={{ color: '#FF6B35' }} />,
        color: 'orange'
      })),
      ...documents.slice(0, 3).map(d => ({
        type: 'document',
        title: `Document uploaded: ${d.original_filename}`,
        time: new Date(d.created_at),
        icon: <FilePdfOutlined style={{ color: '#6B7280' }} />,
        color: 'gray'
      }))
    ].sort((a, b) => b.time - a.time).slice(0, 5);

    return activities;
  }, [projects, documents]);

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="app-content-redesign">
      {/* Dashboard Header */}
      <div className="dashboard-header-redesign">
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle">
          Real-time project metrics and extraction analytics
        </p>
      </div>

      {/* Stats Grid - Industrial Metrics */}
      <div className="stats-grid-redesign">
        <div className="stat-card-redesign">
          <div className="stat-header">
            <div className="stat-icon-redesign">
              <ProjectOutlined />
            </div>
          </div>
          <div className="stat-value-redesign">{metrics.activeProjects.length}</div>
          <div className="stat-label-redesign">Active Projects</div>
          <div className="stat-change">
            <span className="stat-change-value positive">
              {metrics.totalProjects > 0 ? `+${metrics.activeProjects.length}` : '0'}
            </span>
            <span className="stat-change-label">of {metrics.totalProjects} total</span>
          </div>
        </div>

        <div className="stat-card-redesign">
          <div className="stat-header">
            <div className="stat-icon-redesign">
              <FilePdfOutlined />
            </div>
          </div>
          <div className="stat-value-redesign">{metrics.totalDocuments}</div>
          <div className="stat-label-redesign">Total Documents</div>
          <div className="stat-change">
            <span className="stat-change-value positive">{metrics.totalStorageMB} MB</span>
            <span className="stat-change-label">total storage</span>
          </div>
        </div>

        <div className="stat-card-redesign">
          <div className="stat-header">
            <div className="stat-icon-redesign">
              <CheckCircleOutlined />
            </div>
          </div>
          <div className="stat-value-redesign">{metrics.processedDocs}</div>
          <div className="stat-label-redesign">Processed</div>
          <div className="stat-change">
            <div style={{ width: '100%', height: '4px', background: 'rgba(0,0,0,0.08)', borderRadius: '2px', overflow: 'hidden', marginTop: '8px' }}>
              <div style={{ width: `${metrics.processingRate}%`, height: '100%', background: '#FF6B35', transition: 'width 0.3s ease' }}></div>
            </div>
            <span className="stat-change-label" style={{ marginTop: '4px', display: 'block' }}>{metrics.processingRate}% completion</span>
          </div>
        </div>

        <div className="stat-card-redesign">
          <div className="stat-header">
            <div className="stat-icon-redesign">
              <ClockCircleOutlined />
            </div>
          </div>
          <div className="stat-value-redesign">{metrics.pendingDocs}</div>
          <div className="stat-label-redesign">Pending</div>
          <div className="stat-change">
            <span className="stat-change-value">{metrics.pendingDocs > 0 ? 'In Queue' : 'All Clear'}</span>
            <span className="stat-change-label">awaiting processing</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="section-header-redesign">
        <h2 className="section-title-redesign">Quick Actions</h2>
      </div>
      
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            hoverable
            onClick={() => onNavigate('projects')}
            style={{ 
              height: '100%',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <div style={{ 
              width: '56px', 
              height: '56px', 
              margin: '0 auto 16px',
              background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 107, 53, 0.05) 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FF6B35',
              fontSize: '28px'
            }}>
              <PlusOutlined />
            </div>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 700, 
              margin: '0 0 8px 0',
              color: '#1A1D23'
            }}>
              New Project
            </h3>
            <p style={{ 
              fontSize: '13px', 
              color: '#6B7280', 
              margin: 0 
            }}>
              Create a new construction project
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            hoverable
            onClick={() => onNavigate('documents')}
            style={{ 
              height: '100%',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <div style={{ 
              width: '56px', 
              height: '56px', 
              margin: '0 auto 16px',
              background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(107, 114, 128, 0.05) 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              fontSize: '28px'
            }}>
              <UploadOutlined />
            </div>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 700, 
              margin: '0 0 8px 0',
              color: '#1A1D23'
            }}>
              Upload PDF
            </h3>
            <p style={{ 
              fontSize: '13px', 
              color: '#6B7280', 
              margin: 0 
            }}>
              Add construction plans
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            hoverable
            onClick={() => onNavigate('projects')}
            style={{ 
              height: '100%',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <div style={{ 
              width: '56px', 
              height: '56px', 
              margin: '0 auto 16px',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#3B82F6',
              fontSize: '28px'
            }}>
              <FolderOpenOutlined />
            </div>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 700, 
              margin: '0 0 8px 0',
              color: '#1A1D23'
            }}>
              View Projects
            </h3>
            <p style={{ 
              fontSize: '13px', 
              color: '#6B7280', 
              margin: 0 
            }}>
              Browse all projects
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            hoverable
            onClick={() => onNavigate('takeoffs')}
            style={{ 
              height: '100%',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <div style={{ 
              width: '56px', 
              height: '56px', 
              margin: '0 auto 16px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#10B981',
              fontSize: '28px'
            }}>
              <ThunderboltOutlined />
            </div>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 700, 
              margin: '0 0 8px 0',
              color: '#1A1D23'
            }}>
              Takeoffs
            </h3>
            <p style={{ 
              fontSize: '13px', 
              color: '#6B7280', 
              margin: 0 
            }}>
              View extracted items
            </p>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity Section */}
      <div className="section-header-redesign" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="section-title-redesign">Recent Projects</h2>
        <Button 
          type="link" 
          icon={<RightOutlined />} 
          iconPosition="end"
          onClick={() => onNavigate('projects')}
          style={{ padding: 0, fontWeight: 600 }}
        >
          View all
        </Button>
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} lg={12}>
          <Card 
            variant="borderless"
            style={{ height: '100%' }}
          >
            {recentProjects.length > 0 ? (
              <List
                dataSource={recentProjects}
                renderItem={(project) => (
                  <List.Item
                    style={{ 
                      padding: '16px 0', 
                      cursor: 'pointer',
                      transition: 'all 150ms',
                      borderRadius: '8px',
                      marginBottom: '4px'
                    }}
                    className="hover-bg"
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          style={{ 
                            background: 'linear-gradient(135deg, #FF6B35 0%, #E85A28 100%)',
                            width: '40px',
                            height: '40px'
                          }}
                          icon={<ProjectOutlined />}
                        />
                      }
                      title={
                        <div style={{ fontWeight: 700, color: '#1A1D23', fontSize: '14px' }}>
                          {project.name}
                        </div>
                      }
                      description={
                        <Space size={4}>
                          <span style={{ fontSize: '13px', color: '#6B7280' }}>
                            {project.location || 'No location'}
                          </span>
                          <span style={{ color: '#D1D5DB' }}>•</span>
                          <span style={{ fontSize: '13px', color: '#6B7280' }}>
                            {project.documents?.length || 0} docs
                          </span>
                        </Space>
                      }
                    />
                    <Tag color={project.is_active ? 'success' : 'default'}>
                      {project.is_active ? 'Active' : 'Inactive'}
                    </Tag>
                  </List.Item>
                )}
              />
            ) : (
              <Empty 
                description="No projects yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
      </Row>

      <div className="section-header-redesign" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="section-title-redesign">Recent Documents</h2>
        <Button 
          type="link" 
          icon={<RightOutlined />} 
          iconPosition="end"
          onClick={() => onNavigate('documents')}
          style={{ padding: 0, fontWeight: 600 }}
        >
          View all
        </Button>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card 
            variant="borderless"
            style={{ height: '100%' }}
          >
            {recentDocuments.length > 0 ? (
              <List
                dataSource={recentDocuments}
                renderItem={(doc) => (
                  <List.Item
                    style={{ 
                      padding: '16px 0',
                      cursor: 'pointer',
                      transition: 'all 150ms',
                      borderRadius: '8px',
                      marginBottom: '4px'
                    }}
                    className="hover-bg"
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          style={{ 
                            background: '#F3F4F6',
                            color: '#6B7280',
                            width: '40px',
                            height: '40px'
                          }}
                          icon={<FilePdfOutlined />}
                        />
                      }
                      title={
                        <div style={{ fontWeight: 700, color: '#1A1D23', fontSize: '14px' }}>
                          {doc.original_filename}
                        </div>
                      }
                      description={
                        <Space size={4}>
                          <span style={{ fontSize: '13px', color: '#6B7280' }}>
                            {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <span style={{ color: '#D1D5DB' }}>•</span>
                          <span style={{ fontSize: '13px', color: '#6B7280' }}>
                            {new Date(doc.created_at).toLocaleDateString()}
                          </span>
                        </Space>
                      }
                    />
                    <Tag color={doc.processed ? 'success' : 'processing'}>
                      {doc.processed ? 'Processed' : 'Processing'}
                    </Tag>
                  </List.Item>
                )}
              />
            ) : (
              <Empty 
                description="No documents yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Activity Timeline */}
      {activityTimeline.length > 0 && (
        <>
          <Divider style={{ margin: '32px 0 24px 0' }} />
          <div className="section-header-redesign">
            <h2 className="section-title-redesign">Activity Timeline</h2>
          </div>
          <Card variant="borderless">
            <Timeline
              items={activityTimeline.map((item, index) => ({
                color: item.color,
                dot: item.icon,
                children: (
                  <div key={index}>
                    <div style={{ fontWeight: 600, color: '#1A1D23', marginBottom: 4 }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      {formatTimeAgo(item.time)}
                    </div>
                  </div>
                )
              }))}
            />
          </Card>
        </>
      )}
    </div>
  );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;
